// src/components/layout/sidebar.tsx
import { useEffect, useMemo, useState } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/cn';
import { NAV_GROUPS, navGroupsFor, type NavItem } from './nav-items';
import { queueCountFor, useQueueCounts } from './queue-counts';
import { useAuthStore } from '@/auth/auth-store';
import { useMyCapabilities, verdictOf } from '@/auth/capabilities';
import { SECTION_HELP } from '@/lib/glossary';

const STORAGE_KEY = 'ko-admin.sidebar.expanded';

function loadExpanded(): Set<string> {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((v): v is string => typeof v === 'string'));
  } catch {
    return new Set();
  }
}

function saveExpanded(expanded: Set<string>) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify([...expanded]));
  } catch {
    // storage no disponible (SSR/tests): ignorar
  }
}

/** true si el pathname vive dentro de alguno de los hijos declarados del item. */
function isChildActive(item: NavItem, pathname: string): boolean {
  return item.children?.some((c) => pathname === c.to || pathname.startsWith(`${c.to}/`)) ?? false;
}

/** true si el item (o un detalle suyo que no es hijo declarado) es la ruta activa. */
function isSelfActive(item: NavItem, pathname: string): boolean {
  if (item.exact) return pathname === item.to;
  if (pathname === item.to) return true;
  return pathname.startsWith(`${item.to}/`) && !isChildActive(item, pathname);
}

const linkBase =
  'relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm outline-none focus-visible:ring-1 focus-visible:ring-gold';

function AccentBar() {
  return <span aria-hidden="true" className="absolute left-0 h-5 w-0.5 rounded-full bg-gold" />;
}

/**
 * How many things wait in the section, next to its name — the same number
 * «Pendientes de hoy» shows for it, so an operator on any screen knows where
 * the work is without going back to the Panel. Drawn only above zero: a row
 * of «0» pills is noise, and a `null` (no queue, or a card the backend cut
 * for this reader) draws nothing at all.
 */
function QueuePill({ count, label }: { count: number | null; label: string }) {
  if (count === null || count <= 0) return null;
  return (
    <span
      aria-label={`${count.toLocaleString('es-MX')} en ${label}`}
      className="ml-auto rounded-full bg-graphite px-1.5 py-px text-2xs font-medium tabular-nums text-text-primary"
    >
      {count > 999 ? '999+' : count.toLocaleString('es-MX')}
    </span>
  );
}

export function Sidebar() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // Only the sections this operator can open (ko-admin#190): the same
  // `SECTION_CAPABILITY` map the router gates on, so a link here never leads to
  // a redirect. By the time the shell renders, the router has already waited
  // for the capabilities answer, so this does not flicker.
  //
  // An unknown role shows everything instead of nothing: a session whose `user`
  // did not survive (an older persisted store, say) would otherwise render an
  // empty sidebar, and a panel with no navigation is a worse failure than a
  // link that answers 403. The backend is the permission boundary either way.
  const role = useAuthStore((s) => s.user?.role ?? null);
  const { data: capabilities } = useMyCapabilities();
  const groups = useMemo(
    () => (role === null ? NAV_GROUPS : navGroupsFor(role, (c) => verdictOf(capabilities, c))),
    [role, capabilities],
  );
  // A store owner's sections have no platform queues, and the status route
  // would answer them 403.
  const status = useQueueCounts(role !== 'USER');
  const countOf = (to: string) => (status ? queueCountFor(status, to) : null);
  const [expanded, setExpanded] = useState<Set<string>>(loadExpanded);

  // Persistencia del estado de expansión
  useEffect(() => {
    saveExpanded(expanded);
  }, [expanded]);

  // Auto-expansión: si la ruta activa vive dentro de un padre, se abre solo
  useEffect(() => {
    const parents = NAV_GROUPS.flatMap((g) => g.items).filter((item) =>
      isChildActive(item, pathname),
    );
    if (parents.length === 0) return;
    setExpanded((prev) => {
      if (parents.every((p) => prev.has(p.to))) return prev;
      const next = new Set(prev);
      for (const p of parents) next.add(p.to);
      return next;
    });
  }, [pathname]);

  const expand = (to: string) => {
    setExpanded((prev) => {
      if (prev.has(to)) return prev;
      const next = new Set(prev);
      next.add(to);
      return next;
    });
  };

  const toggle = (to: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(to)) next.delete(to);
      else next.add(to);
      return next;
    });
  };

  return (
    <aside className="flex w-60 shrink-0 flex-col border-r border-border bg-surface">
      <div className="flex items-center gap-2 px-5 py-5">
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gold text-bg font-bold">
          KO
        </div>
        <div className="leading-tight">
          <div className="text-sm font-semibold text-text-primary">KO Collectibles</div>
          <div className="text-2xs text-text-muted">Consola de administración</div>
        </div>
      </div>
      <nav aria-label="Navegación principal" className="flex-1 space-y-1 overflow-y-auto px-3 py-2">
        {groups.map((group) => (
          <div key={group.id}>
            {group.label !== null && (
              <div className="px-3 pt-4 pb-1 text-2xs font-semibold uppercase tracking-wider text-text-muted">
                {group.label}
              </div>
            )}
            <ul className="space-y-1">
              {group.items.map((item) => {
                const Icon = item.icon;
                if (!item.enabled) {
                  return (
                    <li key={item.to}>
                      <div
                        className="flex items-center justify-between rounded-lg px-3 py-2 text-sm text-text-muted/60"
                        title="Próximamente"
                      >
                        <span className="flex items-center gap-3">
                          <Icon size={18} aria-hidden />
                          {item.label}
                        </span>
                        <span className="text-2xs uppercase tracking-wide">Próximamente</span>
                      </div>
                    </li>
                  );
                }

                const selfActive = isSelfActive(item, pathname);
                const childActive = isChildActive(item, pathname);

                if (!item.children || item.children.length === 0) {
                  return (
                    <li key={item.to}>
                      <Link
                        to={item.to}
                        activeOptions={{ exact: item.exact ?? false }}
                        title={SECTION_HELP[item.to]}
                        className={cn(
                          linkBase,
                          selfActive
                            ? 'bg-graphite text-text-primary'
                            : 'text-text-secondary hover:bg-graphite hover:text-text-primary',
                        )}
                      >
                        {selfActive && <AccentBar />}
                        <Icon size={18} aria-hidden />
                        {item.label}
                        <QueuePill count={countOf(item.to)} label={item.label} />
                      </Link>
                    </li>
                  );
                }

                const open = expanded.has(item.to);
                const panelId = `sidebar-sub${item.to.replace(/\//g, '-')}`;
                return (
                  <li key={item.to}>
                    <div className="flex items-center gap-1">
                      <Link
                        to={item.to}
                        activeOptions={{ exact: true }}
                        onClick={() => expand(item.to)}
                        title={SECTION_HELP[item.to]}
                        className={cn(
                          linkBase,
                          'flex-1',
                          selfActive
                            ? 'bg-graphite text-text-primary'
                            : childActive
                              ? 'text-text-primary hover:bg-graphite'
                              : 'text-text-secondary hover:bg-graphite hover:text-text-primary',
                        )}
                      >
                        {selfActive && <AccentBar />}
                        <Icon size={18} aria-hidden />
                        {item.label}
                        {/* Folded, the parent carries what waits under it; open,
                            each child shows its own and the sum would double it. */}
                        {!open && <QueuePill count={countOf(item.to)} label={item.label} />}
                      </Link>
                      <button
                        type="button"
                        aria-expanded={open}
                        aria-controls={panelId}
                        aria-label={`${open ? 'Contraer' : 'Expandir'} ${item.label}`}
                        onClick={() => toggle(item.to)}
                        className="rounded-md p-1 text-text-muted outline-none hover:bg-graphite hover:text-text-primary focus-visible:ring-1 focus-visible:ring-gold"
                      >
                        <ChevronDown
                          size={16}
                          className={cn('transition-transform', !open && '-rotate-90')}
                        />
                      </button>
                    </div>
                    {open && (
                      <ul
                        id={panelId}
                        className="ml-[26px] mt-0.5 space-y-0.5 border-l border-border pl-3"
                      >
                        {item.children.map((child) => {
                          const childIsActive =
                            pathname === child.to || pathname.startsWith(`${child.to}/`);
                          return (
                            <li key={child.to}>
                              <Link
                                to={child.to}
                                activeOptions={{ exact: true }}
                                title={SECTION_HELP[child.to]}
                                className={cn(
                                  'relative flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm outline-none focus-visible:ring-1 focus-visible:ring-gold',
                                  childIsActive
                                    ? 'bg-graphite text-text-primary'
                                    : 'text-text-secondary hover:bg-graphite hover:text-text-primary',
                                )}
                              >
                                {childIsActive && <AccentBar />}
                                {child.label}
                                <QueuePill count={countOf(child.to)} label={child.label} />
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>
    </aside>
  );
}
